import { authUrl, apiUrl } from '../config/apiConfig';
import {
  Settings,
  CryptoStat,
  StatsInChartFormat,
  Asset,
  AssetType,
  EquityStat,
  User
} from '../../../api/src/interfaces/shared';

export const urls = {
  facebookAuth: `${authUrl}/facebook`,
  googleAuth: `${authUrl}/google`,
  testUserAuth: `${authUrl}/test`,
  logout: `${apiUrl}/logout`,
  isAuthenticated: `${apiUrl}/isAuthenticated`,
  user: `${apiUrl}/user`,
  userStats: `${apiUrl}/user/stats`,
  settings: `${apiUrl}/user/settings`,
  userEquities: `${apiUrl}/user/equities`,
  visibility: `${apiUrl}/user/visibility`,
  currentStats: `${apiUrl}/user/currentStats`,
  allUsers: `${apiUrl}/users`,
  equity: `${apiUrl}/equity`,
  sellEquity: `${apiUrl}/equity/sell`,
  deleteEquity: `${apiUrl}/equity/delete`,
  crypto: `${apiUrl}/crypto`,
  sellCrypto: `${apiUrl}/crypto/sell`,
  deleteCrypto: `${apiUrl}/crypto/delete`,
  ticker: `${apiUrl}/ticker`,
  currentTicker: `${apiUrl}/ticker/current`
};

type HttpMethod = 'GET' | 'POST';

/**
 * API wrapper for dhhs-server
 */
export class Server {
  static async getStats(id?: string): Promise<StatsInChartFormat> {
    const url = id ? `${urls.userStats}/${id}` : urls.userStats;
    return Server.request(url);
  }

  static async getCurrentStats(id?: string): Promise<StatsInChartFormat> {
    const url = id ? `${urls.currentStats}/${id}` : urls.currentStats;
    return Server.request(url);
  }

  static async loadSettings(): Promise<Settings> {
    return Server.request(urls.user);
  }

  static async updateSettings(settings: Settings) {
    return Server.request(urls.settings, settings, 'POST');
  }

  static async getCurrentTicker(): Promise<number> {
    return Server.request(urls.currentTicker);
  }

  static async getAssets(): Promise<Asset[]> {
    return Server.request(urls.userEquities);
  }

  static async getAllMembers(): Promise<User[]> {
    return Server.request(urls.allUsers);
  }

  static async getMemberPortfolio(userId?: number): Promise<Asset[]> {
    return Server.request(`${urls.userEquities}/${userId}`);
  }

  static async getEquityChart(
    type: AssetType,
    externalId: string
  ): Promise<EquityStat[]> {
    return Server.request(`${urls.equity}/${type}/${externalId}`);
  }

  static async getCryptoTickerChart(
    ticker: string,
    period: string
  ): Promise<CryptoStat[]> {
    const url = `${urls.crypto}/${ticker}/${period}`;
    return Server.request(url);
  }

  static async request(
    path: string,
    fields: any = null,
    method: HttpMethod = 'GET'
  ) {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'include'
    };

    if (fields !== null && method !== 'GET') {
      options.body = JSON.stringify(fields);
    }

    const response = await fetch(path, options);
    const json = await response.json();
    if (response.status >= 400) {
      throw new Error(json);
    }
    return json;
  }
}
